import { FC, useEffect, useState } from "react";
import { useSingleUserControl } from "../../../context/SuperAdmin/SingleUserControlContext";
import {
  Box,
  Button,
  FormControl,
  MenuItem,
  Modal,
  Select,
  Typography,
} from "@mui/material";
import { iDataUserRoles } from "../../../interfaces/SuperAdmin/Pages/iDataUserRoles";
import { iDataUserSuscription } from "../../../interfaces/SuperAdmin/Pages/iDataUserSuscription";

interface Props {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

const DetachModal: FC<Props> = ({ isOpen, setIsOpen }) => {
  const { suscriptions, myRoles, myOrgs, isDetaching, detachUser } =
    useSingleUserControl();

  const [selectedOrgId, setSelectedOrgId] = useState<string>("");
  const [canDetach, setCanDetach] = useState(false);
  const [orgSuscriptions, setOrgSuscriptions] = useState<
    iDataUserSuscription[]
  >([]);
  const [orgRoles, setOrgRoles] = useState<iDataUserRoles[]>([]);

  const handleClose = () => {
    if (!isDetaching) {
      setIsOpen(false);
      setSelectedOrgId("");
    }
  };

  // Initialize selectedOrgId when modal opens
  useEffect(() => {
    if (isOpen) {
      if (myOrgs.length === 1) {
        setSelectedOrgId(myOrgs[0].id_organizacion);
      } else {
        setSelectedOrgId("");
      }
    }
  }, [isOpen, myOrgs]);

  // Filter subscriptions and roles for the selected organization
  useEffect(() => {
    if (selectedOrgId) {
      const filteredSus = suscriptions.filter((sus) => {
        // If exact match
        if (sus.id_organizacion === selectedOrgId) return true;
        // Fallback: If user only has 1 org and subscription has no org ID, assume it belongs to this one
        if (myOrgs.length === 1 && !sus.id_organizacion) return true;
        return false;
      });
      const filteredRoles = myRoles.filter((rol) => {
        if (rol.id_organizacion === selectedOrgId) return true;
        if (myOrgs.length === 1 && !rol.id_organizacion) return true;
        return false;
      });
      setOrgSuscriptions(filteredSus);
      setOrgRoles(filteredRoles);
      console.log("Suscripciones de la org seleccionada:", filteredSus);
      console.log("Roles de la org seleccionada:", filteredRoles);
    } else {
      setOrgSuscriptions([]);
      setOrgRoles([]);
    }
  }, [selectedOrgId, suscriptions, myRoles]);

  // Determine if detaching is allowed for the selected organization
  useEffect(() => {
    if (!selectedOrgId) {
      setCanDetach(false);
      return;
    }

    const activeSus = Boolean(orgSuscriptions.length);
    const activeRoles = Boolean(orgRoles.length);
    // Note: mySuborgs logic is omitted unless we can filter by orgId.
    // Assuming blocking only on filtered items as per user request.

    if (activeSus || activeRoles) {
      setCanDetach(false);
      console.log("No se puede desanexar de la org seleccionada");
    } else {
      setCanDetach(true);
      console.log("Se puede desanexar de la org seleccionada");
    }
  }, [selectedOrgId, orgSuscriptions, orgRoles]);

  const handleDetach = async () => {
    if (selectedOrgId) {
      await detachUser(selectedOrgId);
      handleClose();
    }
  };

  const selectedOrg = myOrgs.find(
    (org) => org.id_organizacion === selectedOrgId
  );

  return (
    <Modal
      open={isOpen}
      onClose={handleClose}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: "rgba(0, 0, 0, 0.3)", // Fondo negro con opacidad
      }}
    >
      <Box
        component={"div"}
        sx={{
          width: 400,
          bgcolor: "background.paper", // Fondo blanco del modal
          borderRadius: 2, // Bordes redondeados
          p: 4,
          boxShadow: 24,
          minWidth: "600px",
          display: "flex",
          flexDirection: "column",
          gap: 3,
        }}
      >
        <Typography component={"h3"} sx={{ fontSize: "20px", fontWeight: 600 }}>
          {selectedOrgId
            ? canDetach
              ? `¿Desanexar de "${selectedOrg?.organizacion}"?`
              : `No puedes desanexar de "${selectedOrg?.organizacion}" aún`
            : "Seleccionar organización para desanexar"}
        </Typography>

        {/* Organization Selector */}
        {myOrgs.length > 1 && (
          <Box>
            <Typography variant="body1" sx={{ mb: 1, fontWeight: 500 }}>
              Organización
            </Typography>
            <FormControl fullWidth>
              <Select
                value={selectedOrgId}
                onChange={(e) => setSelectedOrgId(e.target.value)}
                disabled={isDetaching}
                displayEmpty
              >
                <MenuItem value="" disabled>
                  Seleccione una organización
                </MenuItem>
                {myOrgs.map((org) => (
                  <MenuItem
                    key={org.id_organizacion}
                    value={org.id_organizacion}
                  >
                    {org.organizacion}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        )}

        {/* Blocking Entities List */}
        {selectedOrgId && !canDetach && (
          <Box>
            <Typography sx={{ mb: 1 }}>
              Primero debes eliminar al usuario de las siguientes entidades
              relacionadas a esta organización:
            </Typography>
            <ul
              style={{
                margin: 0,
                paddingLeft: "20px",
                display: "flex",
                flexDirection: "column",
                gap: "14px",
              }}
            >
              {orgSuscriptions.length > 0 && (
                <li>
                  <Typography
                    component="span"
                    sx={{ color: "red", fontWeight: "bold" }}
                  >
                    Suscripciones ({orgSuscriptions.length}):
                  </Typography>
                  <ul style={{ margin: 0, paddingLeft: "20px" }}>
                    {orgSuscriptions.map((row, idx) => (
                      <li key={idx}>{row.plan}</li>
                    ))}
                  </ul>
                </li>
              )}
              {orgRoles.length > 0 && (
                <li>
                  <Typography
                    component="span"
                    sx={{ color: "red", fontWeight: "bold" }}
                  >
                    Roles ({orgRoles.length}):
                  </Typography>
                  <ul style={{ margin: 0, paddingLeft: "20px" }}>
                    {orgRoles.map((row, idx) => (
                      <li key={idx}>{row.rol}</li>
                    ))}
                  </ul>
                </li>
              )}
            </ul>
          </Box>
        )}

        {/* Success Message / Action */}
        {selectedOrgId && canDetach && (
          <Typography sx={{ mb: 1 }}>
            El usuario NO podrá acceder a la app mientras no tenga una
            organización
          </Typography>
        )}

        {/* Buttons */}
        <Box
          sx={{
            textAlign: "right",
            display: "flex",
            justifyContent: "flex-end",
            gap: 3,
            mt: 3,
          }}
        >
          <Button
            variant="outlined"
            onClick={handleClose}
            disabled={isDetaching}
            sx={{
              borderRadius: 14,
              border: "1px solid #336B87",
              color: "#336B87",
              "&:hover": { backgroundColor: "#336B87", color: "#EEF3F6" },
            }}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            sx={{ background: "#336B87", borderRadius: 14 }}
            disabled={!selectedOrgId || !canDetach || isDetaching}
            onClick={handleDetach}
          >
            Confirmar
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default DetachModal;
